import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ListChecks, Activity, Sparkles, Flag, TrendingDown, TrendingUp, Minus, CalendarRange } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DOMAIN_DESCRIPTIONS, fmtDate, fmtDelta, pct } from "@/lib/aba";

const DeltaIcon = ({ v }) => {
  if (v === null || v === undefined || v === 0) return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  return v > 0 ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : <TrendingDown className="h-3.5 w-3.5 text-rose-600" />;
};

const deltaTone = (v) => (v === null || v === undefined || v === 0 ? "text-muted-foreground" : v > 0 ? "text-emerald-700" : "text-rose-700");

export const PeriodComparison = ({ confronto, domains }) => {
  if (!confronto) return null;
  const { ultimo, precedente, delta, giorni } = confronto;
  const active = domains.filter((d) => d.confronto && (d.confronto.ultimo.sessioni > 0 || d.confronto.precedente.sessioni > 0));
  return (
    <Card data-testid="period-comparison">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><CalendarRange className="h-5 w-5 text-primary" /> Confronto periodi</h3>
            <p className="text-sm text-muted-foreground">
              Ultimi {giorni} giorni ({fmtDate(ultimo.da)} – {fmtDate(ultimo.a)}) rispetto ai {giorni} precedenti ({fmtDate(precedente.da)} – {fmtDate(precedente.a)}).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <CompareStat label="Sessioni" now={ultimo.sessioni} prev={precedente.sessioni} delta={delta.sessioni} testId="compare-sessions" />
          <CompareStat label="Media corrette" now={pct(ultimo.media)} prev={pct(precedente.media)} delta={delta.media} suffix=" pt" testId="compare-media" />
          <CompareStat label="Indipendenza" now={pct(ultimo.indipendenza)} prev={pct(precedente.indipendenza)} delta={delta.indipendenza} suffix=" pt" testId="compare-independence" />
        </div>
        {active.length > 0 ? (
          <div className="mt-5 rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Dominio</TableHead>
                  <TableHead className="text-right">Sessioni</TableHead>
                  <TableHead className="text-right">Media corrette</TableHead>
                  <TableHead className="text-right">Indipendenza</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {active.map((d) => {
                  const c = d.confronto;
                  return (
                    <TableRow key={d.key} data-testid={`compare-row-${d.key}`}>
                      <TableCell className="font-medium">{d.label}</TableCell>
                      <TableCell className="text-right tabular">
                        <span className="text-muted-foreground">{c.precedente.sessioni}</span> <ArrowRight className="inline h-3 w-3 text-muted-foreground" /> <span className="font-semibold">{c.ultimo.sessioni}</span>
                        <span className={`ml-2 text-xs ${deltaTone(c.delta.sessioni)}`}>{fmtDelta(c.delta.sessioni)}</span>
                      </TableCell>
                      <TableCell className="text-right tabular">
                        <span className="text-muted-foreground">{pct(c.precedente.media)}</span> <ArrowRight className="inline h-3 w-3 text-muted-foreground" /> <span className="font-semibold">{pct(c.ultimo.media)}</span>
                        <span className={`ml-2 text-xs ${deltaTone(c.delta.media)}`}>{fmtDelta(c.delta.media, " pt")}</span>
                      </TableCell>
                      <TableCell className="text-right tabular">
                        <span className="text-muted-foreground">{pct(c.precedente.indipendenza)}</span> <ArrowRight className="inline h-3 w-3 text-muted-foreground" /> <span className="font-semibold">{pct(c.ultimo.indipendenza)}</span>
                        <span className={`ml-2 text-xs ${deltaTone(c.delta.indipendenza)}`}>{fmtDelta(c.delta.indipendenza, " pt")}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="compare-empty">Nessuna sessione negli ultimi {giorni * 2} giorni: il confronto comparirà appena registri nuovi dati.</p>
        )}
      </CardContent>
    </Card>
  );
};

const CompareStat = ({ label, now, prev, delta, suffix = "", testId }) => (
  <div className="rounded-lg bg-muted/60 px-3 py-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className="font-heading text-2xl font-semibold tabular">{now}</span>
      <span className="text-xs text-muted-foreground">da {prev}</span>
    </div>
    <p className={`text-xs font-medium flex items-center gap-1 mt-0.5 ${deltaTone(delta)}`}><DeltaIcon v={delta} /> {fmtDelta(delta, suffix)} rispetto al periodo precedente</p>
  </div>
);

export const PatientSummary = ({ summary, isLoading, onOpenDomain }) => {
  if (isLoading || !summary) {
    return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;
  }
  const { totals, domains, confronto } = summary;
  return (
    <div className="space-y-6" data-testid="patient-summary">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Total icon={ListChecks} label="Azioni totali" value={totals.azioni} />
        <Total icon={Activity} label="Sessioni registrate" value={totals.sessioni} />
        <Total icon={CheckCircle2} label="Azioni acquisite" value={totals.acquisite} sub={totals.azioni ? `${Math.round((totals.acquisite / totals.azioni) * 100)}% del totale` : null} accent />
        <Total icon={Sparkles} label="Criterio raggiunto" value={totals.suggerimenti} sub="da segnare acquisite" warn={totals.suggerimenti > 0} />
        <Total icon={Flag} label="Obiettivi in ritardo" value={totals.in_ritardo} sub={totals.in_scadenza ? `${totals.in_scadenza} in scadenza` : "rispetto al piano"} danger={totals.in_ritardo > 0} testId="total-late" />
      </div>

      <PeriodComparison confronto={confronto} domains={domains} />

      <div>
        <h3 className="font-heading font-semibold text-lg mb-3">Progressi per dominio</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {domains.map((d, i) => {
            const progress = d.azioni ? Math.round((d.acquisite / d.azioni) * 100) : 0;
            const c = d.confronto;
            const hasCompare = c && (c.ultimo.sessioni > 0 || c.precedente.sessioni > 0);
            return (
              <motion.div key={d.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.03 }}>
                <Card className="h-full transition-shadow duration-200 hover:shadow-md" data-testid={`summary-domain-${d.key}`}>
                  <CardContent className="p-5 flex flex-col h-full">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-heading font-semibold">{d.label}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{DOMAIN_DESCRIPTIONS[d.key]}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {d.in_ritardo > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-1.5 py-0.5" data-testid={`summary-late-${d.key}`}><Flag className="h-3 w-3" /> {d.in_ritardo} in ritardo</span>}
                        {d.suggerimenti > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5"><Sparkles className="h-3 w-3" /> {d.suggerimenti}</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                      <Mini label="Azioni" value={d.azioni} />
                      <Mini label="Acquisite" value={d.acquisite} />
                      <Mini label="Sessioni" value={d.sessioni} />
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                      <span>Media {pct(d.media)} · ultima {fmtDate(d.ultima_sessione)}</span>
                    </div>
                    {hasCompare && (
                      <div className="mt-3 rounded-lg bg-muted/50 px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid={`summary-compare-${d.key}`}>
                        <span className="text-muted-foreground">Ultimi {c.giorni} gg vs precedenti</span>
                        <span className="flex items-center gap-3 tabular">
                          <span className={`flex items-center gap-1 ${deltaTone(c.delta.sessioni)}`} title="Sessioni"><DeltaIcon v={c.delta.sessioni} /> {fmtDelta(c.delta.sessioni)} sess.</span>
                          <span className={`flex items-center gap-1 ${deltaTone(c.delta.media)}`} title="Media corrette"><DeltaIcon v={c.delta.media} /> {fmtDelta(c.delta.media, " pt")}</span>
                        </span>
                      </div>
                    )}
                    {d.azioni_dettaglio.length > 0 && (
                      <ul className="mt-3 space-y-1 text-sm">
                        {d.azioni_dettaglio.slice(0, 3).map((a) => (
                          <li key={a.action_id} className="flex items-center justify-between gap-2">
                            <span className="truncate flex items-center gap-1.5">{a.in_ritardo && <Flag className="h-3 w-3 text-rose-600 shrink-0" />}{a.nome}</span>
                            <span className={`tabular text-xs font-medium ${a.stato === "acquisito" ? "text-emerald-700" : "text-muted-foreground"}`}>{a.n_sessioni} sess. · {pct(a.ultima)}</span>
                          </li>
                        ))}
                        {d.azioni_dettaglio.length > 3 && <li className="text-xs text-muted-foreground">+{d.azioni_dettaglio.length - 3} altre</li>}
                      </ul>
                    )}
                    <Button variant="ghost" size="sm" className="mt-auto self-start -ml-2 gap-1 text-primary" onClick={() => onOpenDomain(d.key)} data-testid={`summary-open-${d.key}`}>Apri dominio <ArrowRight className="h-4 w-4" /></Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Total = ({ icon: Icon, label, value, sub, accent, warn, danger, testId }) => (
  <Card className="shadow-none">
    <CardContent className="p-4 flex items-start gap-3">
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${danger ? "bg-rose-50 text-rose-700" : warn ? "bg-amber-50 text-amber-700" : accent ? "bg-emerald-50 text-emerald-700" : "bg-accent text-primary"}`}><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-semibold tabular leading-tight" data-testid={testId}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const Mini = ({ label, value }) => (
  <div className="rounded-lg bg-muted/60 py-2">
    <p className="font-heading font-semibold tabular">{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
  </div>
);
